import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Button from "react-bootstrap/Button";
import FlashAlert from "../FlashAlert/FlashAlert";
import apiService from "../../services/api.service";
import "./homePollPanel.css";

const POLL_CHECK_MS = 5000;

const HomePollPanel = ({ eventId, isLoggedIn = false }) => {
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const activePollId = poll?.id;
  const activePollRemaining = poll?.remaining_seconds;

  useEffect(() => {
    if (!eventId) {
      setPoll(null);
      setLoading(false);
      return undefined;
    }

    const checkPoll = async () => {
      try {
        const response = await apiService.getActivePoll(eventId);
        const activePoll = response.data || null;
        setPoll((current) => {
          if (current?.id !== activePoll?.id) setAnswered(false);
          return activePoll;
        });
        setError("");
      } catch (err) {
        if (err.response?.status === 404) {
          setPoll(null);
        }
      } finally {
        setLoading(false);
      }
    };

    checkPoll();
    const interval = setInterval(checkPoll, POLL_CHECK_MS);
    return () => clearInterval(interval);
  }, [eventId]);

  useEffect(() => {
    if (!activePollId || activePollRemaining == null) {
      setRemainingSeconds(null);
      return undefined;
    }
    setRemainingSeconds(activePollRemaining);
    const interval = setInterval(() => {
      setRemainingSeconds((current) => {
        const next = Math.max(0, (current ?? 0) - 1);
        if (next === 0) setPoll(null);
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [activePollId, activePollRemaining]);

  const handleRespond = async (optionId) => {
    if (!poll || !isLoggedIn) return;
    setSubmitting(true);
    setError("");
    try {
      await apiService.respondPoll(poll.id, optionId);
      setAnswered(true);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.detail === "already responded") {
        setAnswered(true);
      } else if (err.response?.status === 409) {
        setPoll(null);
      } else {
        setError("Invio risposta non riuscito.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <aside className="home-poll-panel" aria-label="Sondaggi">
      <h2 className="home-poll-panel__title">Sondaggi</h2>

      {loading ? (
        <p className="home-poll-panel__empty">Caricamento...</p>
      ) : poll ? (
        <>
          {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
          {answered ? (
            <p className="home-poll-panel__empty">Grazie, risposta inviata.</p>
          ) : (
            <>
              <p className="home-poll-panel__question">{poll.question}</p>
              {remainingSeconds !== null && (
                <p className="home-poll-panel__timer">
                  Tempo rimasto: {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, "0")}
                </p>
              )}
              <div className="home-poll-panel__options">
                {poll.options?.map((option) => (
                  isLoggedIn ? (
                    <Button
                      key={option.id}
                      variant="outline-primary"
                      className="home-poll-panel__option"
                      disabled={submitting}
                      onClick={() => handleRespond(option.id)}
                    >
                      {option.label}
                    </Button>
                  ) : (
                    <Button
                      key={option.id}
                      as={Link}
                      to="/login"
                      variant="outline-primary"
                      className="home-poll-panel__option"
                    >
                      {option.label}
                    </Button>
                  )
                ))}
              </div>
              {!isLoggedIn && (
                <p className="home-poll-panel__hint">
                  <Link to="/login">Accedi</Link> per partecipare al sondaggio.
                </p>
              )}
            </>
          )}
        </>
      ) : (
        <p className="home-poll-panel__empty">Nessun sondaggio attivo al momento.</p>
      )}
    </aside>
  );
};

export default HomePollPanel;
